// Who is acting, and who may see a request. Roles come only from the token's `roles` claim and the
// department head from the Departments list, both resolved by the broker (03-SECURITY); nothing
// here trusts the browser. Status changes themselves are in status.ts.

import type { AppRole } from '../roles';
import { allow, deny, type Decision } from './decision';
import { POST_PO_OPEN, type DeliveryState, type PrStatus } from './statuses';

export interface Actor {
  upn: string;
  roles: ReadonlySet<AppRole>;
}

/** Head and backup of the requester's department (Departments list). */
export interface DeptHead {
  headUPN?: string | undefined;
  backupUPN?: string | undefined;
}

/** The parties a transition can be performed by. */
export type Party =
  'requester' | 'procurement' | 'deptHead' | 'cfo' | 'finance' | 'verticalApprover';

export function sameUpn(a: string | undefined, b: string | undefined): boolean {
  return !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
}

export function hasRole(actor: Actor, role: AppRole): boolean {
  return actor.roles.has(role);
}

export function isParty(
  party: Party,
  actor: Actor,
  requesterUPN: string,
  deptHead?: DeptHead,
  verticalApprover?: DeptHead,
): boolean {
  switch (party) {
    case 'requester':
      return sameUpn(actor.upn, requesterUPN);
    case 'procurement':
      return hasRole(actor, 'Procurement');
    case 'cfo':
      return hasRole(actor, 'CFO');
    case 'finance':
      return hasRole(actor, 'Finance');
    case 'deptHead':
      return sameUpn(actor.upn, deptHead?.headUPN) || sameUpn(actor.upn, deptHead?.backupUPN);
    case 'verticalApprover':
      return (
        sameUpn(actor.upn, verticalApprover?.headUPN) ||
        sameUpn(actor.upn, verticalApprover?.backupUPN)
      );
  }
}

const CFO_VISIBLE: ReadonlySet<PrStatus> = new Set<PrStatus>([
  'PendingCFO',
  'PendingVertical',
  ...POST_PO_OPEN,
  'Closed',
]);
const FINANCE_VISIBLE: ReadonlySet<PrStatus> = new Set<PrStatus>([
  'Invoiced',
  'VoucherWithFinance',
  'Paid',
  'Closed',
]);

export interface PrView {
  requesterUPN: string;
  department: string;
  status: PrStatus;
}

/**
 * Read access to one request. Procurement sees all; the requester their own; a department head
 * (or backup) their department's; the CFO from PO approval on; Finance from invoicing on.
 * Admin and MgmtViewer read the dashboard and audit log, not request records.
 */
export function canViewPr(
  actor: Actor,
  pr: PrView,
  headOfDepartments: readonly string[] = [],
): boolean {
  if (hasRole(actor, 'Procurement')) return true;
  if (sameUpn(actor.upn, pr.requesterUPN)) return true;
  if (headOfDepartments.includes(pr.department)) return true;
  if (hasRole(actor, 'CFO') && CFO_VISIBLE.has(pr.status)) return true;
  return hasRole(actor, 'Finance') && FINANCE_VISIBLE.has(pr.status);
}

export interface PoSignFacts {
  status: PrStatus;
  requesterUPN: string;
  sentBeforeCFO: boolean;
  cfoSigned: boolean;
  preparedBy: string;
}

/** CFO signature on a PO that was sent first (small or rush). Status is then re-derived. */
export function canSignSentPo(actor: Actor, po: PoSignFacts): Decision<'signed'> {
  if (!hasRole(actor, 'CFO')) return deny('forbidden', 'Only the CFO signs purchase orders.');
  if (!POST_PO_OPEN.includes(po.status) || !po.sentBeforeCFO || po.cfoSigned) {
    return deny('invalid_transition', 'This purchase order is not waiting for a CFO signature.');
  }
  if (sameUpn(actor.upn, po.preparedBy)) {
    return deny('preparer_cannot_approve', 'The person who prepared the PO cannot sign it.');
  }
  return allow('signed', sameUpn(actor.upn, po.requesterUPN) ? ['cfo_own_request'] : []);
}

/** Delivery confirmation: the requester, or Procurement on their behalf (flagged and logged). */
export function canConfirmDelivery(
  actor: Actor,
  pr: { status: PrStatus; requesterUPN: string },
  delivery: DeliveryState,
): Decision<'confirm'> {
  if (!POST_PO_OPEN.includes(pr.status) || delivery === 'full' || delivery === 'notRequired') {
    return deny('invalid_transition', 'Nothing is waiting to be received on this request.');
  }
  if (sameUpn(actor.upn, pr.requesterUPN)) return allow('confirm');
  if (hasRole(actor, 'Procurement')) return allow('confirm', ['on_behalf']);
  return deny('forbidden', 'Only the requester or Procurement can confirm delivery.');
}

/** Supplier invoices are recorded by Procurement once a PO is out. */
export function canRecordInvoice(actor: Actor, status: PrStatus): Decision<'record'> {
  if (!hasRole(actor, 'Procurement'))
    return deny('forbidden', 'Only Procurement records invoices.');
  if (!POST_PO_OPEN.includes(status)) {
    return deny('invalid_transition', 'Invoices can only be recorded after the PO is sent.');
  }
  return allow('record');
}

/** D75: a buyer is working a request they raised (refused everywhere; a colleague handles it). */
export function isOwnRequest(actor: Actor, requesterUPN: string | undefined): boolean {
  return sameUpn(actor.upn, requesterUPN);
}
